import React, { useState, useEffect, useRef, useCallback } from "react";
import { connect } from "react-redux";
import { TabPanel, Item as TabItem } from 'devextreme-react/tab-panel';
import {
    DataGrid,
    Column,
    Selection,
    Sorting,
    Paging,
    FilterRow,
    LoadPanel as DataGridLoadPanel,
} from "devextreme-react/data-grid";
import { LoadPanel } from 'devextreme-react/load-panel';
import axios from "axios";
import routes from "../../../../../../assets/apiRoutes.json";

function ListAvailableShipmentLotsLocations(props) {
    const { onClose, itemRowId } = props;
    const accessToken = props.oidc.user.access_token;
    
    const [lotsData, setLotsData] = useState([]);
    const [selectedLot, setSelectedLot] = useState(null);
    const [locationData, setLocationData] = useState([]);
    const [itemInventoryStatus, setItemInventoryStatus] = useState(null);
    const [lotInventoryStatus, setLotInventoryStatus] = useState(null);
    const [itemNetWeightStatus, setItemNetWeightStatus] = useState(null);
    const [itemGrossWeightStatus, setItemGrossWeightStatus] = useState(null);
    const [lotNetWeightStatus, setLotNetWeightStatus] = useState(null);
    const [lotGrossWeightStatus, setLotGrossWeightStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    
    const lotsGridRef = useRef(null);
    const locationGridRef = useRef(null);

    // Fetch lots data when itemRowId is available
    useEffect(() => {
        if (!itemRowId) return;
        
        const fetchLotsData = async () => {
            setLoading(true);
            try {
                const response = await axios.get(
                    `${window._env_.REACT_APP_APIS}/${routes.IM.Lot.GetLotItemInventoryStatus}`,
                    {
                        params: { itemId: itemRowId },
                        headers: {
                            Authorization: `Bearer ${accessToken}`
                        }
                    }
                );
                
                let data = [];
                if (response.data) {
                    if (response.data.data && Array.isArray(response.data.data)) {
                        data = response.data.data;
                    } else if (Array.isArray(response.data)) {
                        data = response.data;
                    }
                }
                setLotsData(data);
                
                // Select first row by default
                if (data && data.length > 0) {
                    setSelectedLot(data[0]);
                }
            } catch (error) {
                console.error("Error fetching lots data:", error);
                setLotsData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchLotsData();
    }, [itemRowId, accessToken]);

    // Fetch location data and inventory status when lot is selected
    useEffect(() => {
        if (!selectedLot || !itemRowId) return;

        const fetchLocationData = async () => {
            setLoading(true);
            try {
                
                // Fetch location quantity summary
                const locationResponse = await axios.get(
                    `${window._env_.REACT_APP_APIS}/${routes.IM.GetLocationQuantitySummary}`,
                    {
                        params: {
                            itemId: itemRowId,
                            lotId: selectedLot.RowId
                        },
                        headers: {
                            Authorization: `Bearer ${accessToken}`
                        }
                    }
                );

                let locationDataArray = [];
                if (locationResponse.data) {
                    if (locationResponse.data.data && Array.isArray(locationResponse.data.data)) {
                        locationDataArray = locationResponse.data.data;
                    } else if (Array.isArray(locationResponse.data)) {
                        locationDataArray = locationResponse.data;
                    }
                }
                setLocationData(locationDataArray);

                // Set inventory status from selected lot data (DTO uses PascalCase)
                setItemInventoryStatus({
                    onHand: selectedLot.OnHandItem,
                    inReceiving: selectedLot.InReceivingItem,
                    outShipped: selectedLot.OutShippedItem,
                    outUnavailable: selectedLot.OutUnavailableItem,
                    available: selectedLot.AvailableItem,
                    inOrdered: selectedLot.InOrderedItem,
                    outOrdered: selectedLot.OutOrderedItem,
                    netAvailable: selectedLot.NetAvailableItem,
                    futureRec: selectedLot.FutureRecItem,
                    futureShip: selectedLot.FutureShipItem
                });

                setLotInventoryStatus({
                    onHand: selectedLot.OnHandLot,
                    inReceiving: selectedLot.InReceivingLot,
                    outShipped: selectedLot.OutShippedLot,
                    outUnavailable: selectedLot.OutUnavailableLot,
                    available: selectedLot.AvailableLot,
                    inOrdered: selectedLot.InOrderedLot,
                    outOrdered: selectedLot.OutOrderedLot,
                    netAvailable: selectedLot.NetAvailableLot,
                    futureRec: selectedLot.FutureRecLot,
                    futureShip: selectedLot.FutureShipLot
                });

                // Set Item Net Weight Status
                setItemNetWeightStatus({
                    onHand: selectedLot.OnHandItemNetWeight,
                    inReceiving: selectedLot.InReceivingItemNetWeight,
                    outShipped: selectedLot.OutShippedItemNetWeight,
                    outUnavailable: selectedLot.OutUnavailableItemNetWeight,
                    available: selectedLot.AvailableItemNetWeight,
                    inOrdered: selectedLot.InOrderedItemNetWeight,
                    outOrdered: selectedLot.OutOrderedItemNetWeight,
                    netAvailable: selectedLot.NetAvailableItemNetWeight,
                    futureRec: selectedLot.FutureRecItemNetWeight,
                    futureShip: selectedLot.FutureShipItemNetWeight
                });

                // Set Item Gross Weight Status
                setItemGrossWeightStatus({
                    onHand: selectedLot.OnHandItemGrossWeight,
                    inReceiving: selectedLot.InReceivingItemGrossWeight,
                    outShipped: selectedLot.OutShippedItemGrossWeight,
                    outUnavailable: selectedLot.OutUnavailableItemGrossWeight,
                    available: selectedLot.AvailableItemGrossWeight,
                    inOrdered: selectedLot.InOrderedItemGrossWeight,
                    outOrdered: selectedLot.OutOrderedItemGrossWeight,
                    netAvailable: selectedLot.NetAvailableItemGrossWeight,
                    futureRec: selectedLot.FutureRecItemGrossWeight,
                    futureShip: selectedLot.FutureShipItemGrossWeight
                });

                // Set Lot Net Weight Status
                setLotNetWeightStatus({
                    onHand: selectedLot.OnHandLotNetWeight,
                    inReceiving: selectedLot.InReceivingLotNetWeight,
                    outShipped: selectedLot.OutShippedLotNetWeight,
                    outUnavailable: selectedLot.OutUnavailableLotNetWeight,
                    available: selectedLot.AvailableLotNetWeight,
                    inOrdered: selectedLot.InOrderedLotNetWeight,
                    outOrdered: selectedLot.OutOrderedLotNetWeight,
                    netAvailable: selectedLot.NetAvailableLotNetWeight,
                    futureRec: selectedLot.FutureRecLotNetWeight,
                    futureShip: selectedLot.FutureShipLotNetWeight
                });

                // Set Lot Gross Weight Status
                setLotGrossWeightStatus({
                    onHand: selectedLot.OnHandLotGrossWeight,
                    inReceiving: selectedLot.InReceivingLotGrossWeight,
                    outShipped: selectedLot.OutShippedLotGrossWeight,
                    outUnavailable: selectedLot.OutUnavailableLotGrossWeight,
                    available: selectedLot.AvailableLotGrossWeight,
                    inOrdered: selectedLot.InOrderedLotGrossWeight,
                    outOrdered: selectedLot.OutOrderedLotGrossWeight,
                    netAvailable: selectedLot.NetAvailableLotGrossWeight,
                    futureRec: selectedLot.FutureRecLotGrossWeight,
                    futureShip: selectedLot.FutureShipLotGrossWeight
                });
            } catch (error) {
                console.error("Error fetching location data:", error);
                setLocationData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchLocationData();
    }, [selectedLot, itemRowId, accessToken]);

    const onLotSelectionChanged = useCallback((e) => {
        const selectedRowsData = e.selectedRowsData;
        if (selectedRowsData && selectedRowsData.length > 0) {
            setSelectedLot(selectedRowsData[0]);
        } else {
            setSelectedLot(null);
            setLocationData([]);
            setItemInventoryStatus(null);
            setLotInventoryStatus(null);
            setItemNetWeightStatus(null);
            setItemGrossWeightStatus(null);
            setLotNetWeightStatus(null);
            setLotGrossWeightStatus(null);
        }
    }, []);

    const formatNumber = (value) => {
        if (value == null || value === undefined) return "";
        return parseFloat(value).toFixed(2);
    };

    const renderStatusPanel = (title, status) => {
        if (!status) return null;
        
        return (
            <div style={{ 
                border: '1px solid #ddd', 
                borderRadius: '4px', 
                padding: '15px',
                margin: '10px',
                backgroundColor: '#f9f9f9'
            }}>
                <h4 style={{ marginTop: 0, marginBottom: '15px', fontSize: '14px', fontWeight: 'bold' }}>
                    {title}
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div><strong>On Hand:</strong></div>
                    <div style={{ textAlign: 'right' }}>{formatNumber(status.onHand)}</div>
                    
                    <div><strong>In-Receiving:</strong></div>
                    <div style={{ textAlign: 'right' }}>{formatNumber(status.inReceiving)}</div>
                    
                    <div><strong>In-Shipping:</strong></div>
                    <div style={{ textAlign: 'right' }}>{formatNumber(status.outShipped)}</div>
                    
                    <div><strong>Unavailable:</strong></div>
                    <div style={{ textAlign: 'right' }}>{formatNumber(status.outUnavailable)}</div>
                    
                    <div><strong>Available:</strong></div>
                    <div style={{ textAlign: 'right' }}>{formatNumber(status.available)}</div>
                    
                    <div><strong>Pending Rec:</strong></div>
                    <div style={{ textAlign: 'right' }}>{formatNumber(status.inOrdered)}</div>
                    
                    <div><strong>Pending Ship:</strong></div>
                    <div style={{ textAlign: 'right' }}>{formatNumber(status.outOrdered)}</div>
                    
                    <div><strong>Net Available:</strong></div>
                    <div style={{ textAlign: 'right' }}>{formatNumber(status.netAvailable)}</div>
                    
                    <div><strong>Future Rec:</strong></div>
                    <div style={{ textAlign: 'right' }}>{formatNumber(status.futureRec)}</div>
                    
                    <div><strong>Future Ship:</strong></div>
                    <div style={{ textAlign: 'right' }}>{formatNumber(status.futureShip)}</div>
                </div>
            </div>
        );
    };

    const renderWeightPanel = (title, status) => {
        if (!status) return null;
        
        return (
            <div style={{ 
                border: '1px solid #ddd', 
                borderRadius: '4px', 
                padding: '15px',
                margin: '10px',
                backgroundColor: '#f9f9f9'
            }}>
                <h4 style={{ marginTop: 0, marginBottom: '15px', fontSize: '14px', fontWeight: 'bold' }}>
                    {title}
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div><strong>On Hand:</strong></div>
                    <div style={{ textAlign: 'right' }}>{formatNumber(status.onHand)}</div>
                    
                    <div><strong>In-Receiving:</strong></div>
                    <div style={{ textAlign: 'right' }}>{formatNumber(status.inReceiving)}</div>
                    
                    <div><strong>In-Shipping:</strong></div>
                    <div style={{ textAlign: 'right' }}>{formatNumber(status.outShipped)}</div>
                    
                    <div><strong>Unavailable:</strong></div>
                    <div style={{ textAlign: 'right' }}>{formatNumber(status.outUnavailable)}</div>
                    
                    <div><strong>Available:</strong></div>
                    <div style={{ textAlign: 'right' }}>{formatNumber(status.available)}</div>
                    
                    <div><strong>Pending Rec:</strong></div>
                    <div style={{ textAlign: 'right' }}>{formatNumber(status.inOrdered)}</div>
                    
                    <div><strong>Pending Ship:</strong></div>
                    <div style={{ textAlign: 'right' }}>{formatNumber(status.outOrdered)}</div>
                    
                    <div><strong>Net Available:</strong></div>
                    <div style={{ textAlign: 'right' }}>{formatNumber(status.netAvailable)}</div>
                    
                    <div><strong>Future Rec:</strong></div>
                    <div style={{ textAlign: 'right' }}>{formatNumber(status.futureRec)}</div>
                    
                    <div><strong>Future Ship:</strong></div>
                    <div style={{ textAlign: 'right' }}>{formatNumber(status.futureShip)}</div>
                </div>
            </div>
        );
    };

    const renderWeightSubsection = (title, status) => {
        if (!status) return null;
        
        return (
            <div style={{ 
                border: '1px solid #ddd', 
                borderRadius: '4px', 
                margin: '10px',
                backgroundColor: '#fff'
            }}>
                <div style={{
                    backgroundColor: '#BBDEFB',
                    color: '#000',
                    padding: '8px 12px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    borderRadius: '4px 4px 0 0'
                }}>
                    {title}
                </div>
                <div style={{ padding: '15px' }}>
                    <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <label style={{ flex: '0 0 100px', fontSize: '13px' }}>On Hand:</label>
                        <div style={{ flex: '1', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <input 
                                type="text" 
                                value={formatNumber(status.onHand)} 
                                readOnly
                                style={{ 
                                    flex: '1',
                                    textAlign: 'right',
                                    padding: '4px 8px',
                                    border: '1px solid #ccc',
                                    borderRadius: '3px',
                                    fontSize: '13px'
                                }}
                            />
                            <span style={{ fontSize: '16px', cursor: 'pointer' }}>🧮</span>
                        </div>
                    </div>
                    <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <label style={{ flex: '0 0 100px', fontSize: '13px' }}>Available:</label>
                        <div style={{ flex: '1', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <input 
                                type="text" 
                                value={formatNumber(status.available)} 
                                readOnly
                                style={{ 
                                    flex: '1',
                                    textAlign: 'right',
                                    padding: '4px 8px',
                                    border: '1px solid #ccc',
                                    borderRadius: '3px',
                                    fontSize: '13px'
                                }}
                            />
                            <span style={{ fontSize: '16px', cursor: 'pointer' }}>🧮</span>
                        </div>
                    </div>
                    <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <label style={{ flex: '0 0 100px', fontSize: '13px' }}>Net Available:</label>
                        <div style={{ flex: '1', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <input 
                                type="text" 
                                value={formatNumber(status.netAvailable)} 
                                readOnly
                                style={{ 
                                    flex: '1',
                                    textAlign: 'right',
                                    padding: '4px 8px',
                                    border: '1px solid #ccc',
                                    borderRadius: '3px',
                                    fontSize: '13px'
                                }}
                            />
                            <span style={{ fontSize: '16px', cursor: 'pointer' }}>🧮</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div style={{ padding: "10px", height: "100%", display: "flex", flexDirection: "column" }}>
            <LoadPanel
                visible={loading}
                showIndicator={true}
                showPane={true}
                shading={true}
                hideOnOutsideClick={false}
            />
            
            {/* Top Grid - Lots */}
            <div style={{ flex: '0 0 40%', minHeight: 0, display: 'flex', flexDirection: 'column', marginBottom: '10px' }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Available Lots</h4>
                <DataGrid
                    ref={lotsGridRef}
                    dataSource={lotsData}
                    showBorders={true}
                    height="100%"
                    keyExpr="RowId"
                    onSelectionChanged={onLotSelectionChanged}
                    selectedRowKeys={selectedLot ? [selectedLot.RowId] : []}
                >
                    <DataGridLoadPanel enabled={true} />
                    <Selection mode="single" />
                    <Sorting mode="single" />
                    <Paging enabled={false} />
                    <Column 
                        dataField="LotCode" 
                        caption="Lot" 
                        allowSorting={true}
                        width={150}
                    />
                    <Column 
                        dataField="Description1" 
                        caption="Description" 
                        allowSorting={true}
                    />
                    <Column 
                        dataField="DateIntroduced" 
                        caption="Introduced Date" 
                        allowSorting={true}
                        dataType="datetime"
                        format="MM/dd/yyyy hh:mm:ss a"
                        width={180}
                    />
                    <Column 
                        dataField="AvailableLot" 
                        caption="Available" 
                        allowSorting={true}
                        dataType="number"
                        format="#,##0.00"
                        width={100}
                    />
                </DataGrid>
            </div>

            {/* Bottom Tabs */}
            <div style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', marginTop: '10px', overflow: 'hidden' }}>
                <TabPanel
                    selectedIndex={selectedTabIndex}
                    onSelectionChanged={(e) => setSelectedTabIndex(e.value)}
                    style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}
                >
                    {/* Primary Tab */}
                    <TabItem title="Primary">
                        <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Location Details</h4>
                            <DataGrid
                                ref={locationGridRef}
                                dataSource={locationData}
                                showBorders={true}
                                height="100%"
                                keyExpr="RLocation"
                            >
                                <DataGridLoadPanel enabled={true} />
                                <Sorting mode="single" />
                                <Paging enabled={false} />
                                <Column 
                                    dataField="warehouse" 
                                    caption="WH" 
                                    allowSorting={true}
                                    width={120}
                                />
                                <Column 
                                    dataField="LocationCode" 
                                    caption="Location" 
                                    allowSorting={true}
                                    width={120}
                                />
                                <Column 
                                    dataField="Description" 
                                    caption="Description" 
                                    allowSorting={true}
                                />
                                <Column 
                                    dataField="Available" 
                                    caption="Available" 
                                    allowSorting={true}
                                    dataType="number"
                                    format="#,##0.00"
                                    width={100}
                                />
                            </DataGrid>
                        </div>
                    </TabItem>

                    {/* Status Tab */}
                    <TabItem title="Status">
                        <div style={{ 
                            height: "100%", 
                            display: "flex", 
                            flexDirection: "row",
                            gap: "10px",
                            overflow: "auto"
                        }}>
                            {renderStatusPanel("Item - Quantity Status", itemInventoryStatus)}
                            {renderStatusPanel("Lot - Quantity Status", lotInventoryStatus)}
                        </div>
                    </TabItem>

                    {/* Total Weights Tab */}
                    <TabItem title="Total Weights">
                        <div style={{ 
                            height: "100%", 
                            display: "flex", 
                            flexDirection: "row",
                            gap: "10px",
                            backgroundColor: '#f5f5f5',
                            padding: '10px',
                            overflow: 'auto'
                        }}>
                            {/* Left Section - Item Weights */}
                            <div style={{ 
                                flex: '1', 
                                display: 'flex', 
                                flexDirection: 'column',
                                gap: '10px'
                            }}>
                                {renderWeightSubsection("Item - Total Net Weight", itemNetWeightStatus)}
                                {renderWeightSubsection("Item - Total Gross Weight", itemGrossWeightStatus)}
                            </div>
                            
                            {/* Right Section - Lot Weights */}
                            <div style={{ 
                                flex: '1', 
                                display: 'flex', 
                                flexDirection: 'column',
                                gap: '10px'
                            }}>
                                {renderWeightSubsection("Lot - Total Net Weight", lotNetWeightStatus)}
                                {renderWeightSubsection("Lot - Total Gross Weight", lotGrossWeightStatus)}
                            </div>
                        </div>
                    </TabItem>
                </TabPanel>
            </div>
        </div>
    );
}

function mapStateToProps(state) {
    return {
        ReceiptReducer: state.ReceiptReducer,
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(ListAvailableShipmentLotsLocations);
